import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
} from "react-bootstrap";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import ESInput from "../../controls/ESInput";
import ESSearchDropdown from "../../controls/ESSearchDropdown";
import { UserContext } from "../../routes/routes";
import {
  getOrgTemplateById,
  getEffectiveEmailTemplate,
  insertTemplate,
  tempelateMaster,
  updateTemplate,
} from "../../utils/ApiUrl";
import { axiosPost } from "../../utils/AxiosHelper";
import { Option, checkIfSuperAdmin, copyToClipboard, decrypt, getCachedData } from "../../utils/common";
import { ToastTypeCode, showToast, SystemEmailTemplatesOptions } from "../../utils/Constant";
import Header from "../header/Header";
import Loader from "../loader/Loader";
import ConfirmationPopup from "../popUp/ConfirmationPopup";
import SubHeader from "../subheader/Subheader";
import BackButton from "../../components/Common/BackButton/BackButton";
import { defaultTemplateBody } from "./defaultTempelateBody";
import "./TempelateList.scss";

interface Tempelate {
  TempelateId?: number;
  MasterTemplateId?: any;
  TempelateName: string;
  TempelateBody: string;
  TempelateSubject: string;
  IsDefault: boolean;
  IsActive: boolean;
  OrganizationId: number;
  CreateUserId?: number;
  UpdateUserId?: number | null;
  CreateDate?: string;
  UpdateDate?: string | null;
  InternalKey?: string;
  Source?: string;
}

interface AddTempelateProps {
  tempelateId?: number | null;
  onClose: () => void;
}

const AddTemplate: React.FC<AddTempelateProps> = ({ tempelateId, onClose }) => {
  const methods = useForm<Tempelate>({
    defaultValues: {
      TempelateName: "",
      TempelateBody: "",
      TempelateSubject: "",
      IsDefault: false,
      OrganizationId: 0,
      IsActive: true,
    },
  });
  const {
    control,
    formState: { errors },
  } = methods;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tempelate, setTempelate] = useState<any>();
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [organizationId, setOrganizationId] = useState<number | 0>(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const { appContextdata } = useContext<any>(UserContext);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);

  const validateQuillContent = (value: string): string | true => {
    // const strippedValue = value.replace(/<\/?[^>]+(>|$)/g, "").trim();

    // const requiredTags = {
    //   fullName: { regex: /\[Full Name\]/, message: "[Full Name]" },
    //   formLink: { regex: /\[Form Link\]/, message: "[Form Link]" },
    //   orgName: {
    //     regex: /\[Organization Name\]/,
    //     message: "[Organization Name]",
    //   },
    // };

    // const missingTags: string[] = [];

    // if (strippedValue?.length === 0) {
    //   return "Template body is required";
    // }

    // for (const key in requiredTags) {
    //   if (!requiredTags[key as keyof typeof requiredTags].regex.test(value)) {
    //     missingTags.push(
    //       requiredTags[key as keyof typeof requiredTags].message
    //     );
    //   }
    // }

    // if (missingTags?.length > 0) {
    //   return `Template body must contain ${missingTags.join(", ")}`;
    // }

    return true;
  };

  const quillRef = useRef<ReactQuill>(null);
  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        const tempelateId = await decrypt(id);
        setTempelate(tempelateId);
      };
      fetchData();
    } else {
      methods.setValue("TempelateBody", defaultTemplateBody);
    }
  }, [id]);

  const fetchData = async () => {
    if (appContextdata?.Role) {
      const isAdmin = checkIfSuperAdmin(appContextdata);

      if (isAdmin) {
        setIsSuperAdmin(true);
        const options = (await getCachedData('organizations')) as Option[];
        setOrganizations(options);
      }
    }
  };
  useEffect(() => {
    fetchData();
  }, [appContextdata?.Role]);

  const fetchTemplateData = async (templateId: number | string) => {
    setLoading(true);
    try {
      const response: any = await axiosPost(getEffectiveEmailTemplate, {
        MasterTemplateId: parseInt(templateId as string, 10),
        OrganizationId: organizationId === 0 ? appContextdata?.OrganizationId : organizationId
      });
      if (response?.data?.Status === 200) {
        const data = response.data.ExtraData;

        const organization = organizations?.find(
          (org: Option) => org.value === data.OrganizationId
        );

        const transformedData = {
          ...data,
          OrganizationId: organization ? organization : null,
          MasterTemplateId: SystemEmailTemplatesOptions.find(o => o.value === data.MasterTemplateId) || null,
        };

        methods.reset(transformedData);
        setOrganizationId(data.OrganizationId);
      } else {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error fetching template data", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tempelate) {
      fetchTemplateData(tempelate);
    }
  }, [tempelate, methods, organizations]);

  const onSubmit: SubmitHandler<Tempelate> = async (data) => {
    const transformedData = {
      ...data,
      IsDefault: tempelate ? false : !!data.IsDefault,
      IsActive: !!data.IsActive,
      OrganizationId: organizationId,
      MasterTemplateId: (data.MasterTemplateId as unknown as Option)?.value || 0,
      TempelateId: tempelate ? parseInt(tempelate, 10) : undefined
    };
    setLoading(true);
    try {
      let response: any;

      if (tempelate) {
        response = await axiosPost(updateTemplate, transformedData);
      } else {
        response = await axiosPost(insertTemplate, transformedData);
      }

      if (response?.data?.Status === 200) {
        const message = tempelate
          ? "Template updated successfully"
          : "Template created successfully";
        showToast(message, ToastTypeCode?.SUCCESS);
        navigate(tempelateMaster);
      } else if (response?.data?.ErrorMessage) {
        showToast(`${response?.data?.ErrorMessage}`, ToastTypeCode?.ERROR);
      }
    } catch (error) {
      showToast("Error saving template", ToastTypeCode?.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (selectedOption: Option | null) => {
    setOrganizationId(selectedOption?.value ?? 0);
  };

  const handleCreate: SubmitHandler<Tempelate> = (data) => {
    // const isDefault = methods.watch("IsDefault");
    // if (isDefault) {
    //   setShowCreatePopup(true);
    // }
    setShowCreatePopup(true);
  };

  const handleEdit: SubmitHandler<Tempelate> = (data) => {
    setShowEditPopup(true);
  };

  const confirmCreateTemplate = async () => {
    await methods.handleSubmit(onSubmit)();
    setShowCreatePopup(false);
  };

  const confirmEditTemplate = async () => {
    await methods.handleSubmit(onSubmit)();
    setShowEditPopup(false);
  };

  return (
    <div className="wrap-usertemplate">
      <Header />
      <SubHeader />
      <div className="usertemplate-blk">
        <Container fluid>
          <Card>
            <CardHeader>
              <Row>
                <Col md={6}>
                  <h2 className="page-title">
                    <BackButton fallbackRoute={tempelateMaster} iconOnly={true} />
                    {id ? "Edit Template" : "Add Template"}
                  </h2>
                </Col>
              </Row>
            </CardHeader>
            <CardBody data-tour="fill-template-information">
              {loading ? (
                <Loader />
              ) : (
                <FormProvider {...methods}>
                  <form>
                    <Row>
                      {isSuperAdmin && (
                        <Form.Group as={Col} md="3">
                          <Form.Label>
                            Select Organization{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>

                          <ESSearchDropdown
                            name="OrganizationId"
                            placeholder="Select Organization"
                            options={organizations}
                            required
                            onChange={handleOrganizationChange}
                          />
                        </Form.Group>
                      )}

                      <Form.Group as={Col} md="3">
                        <Form.Label>
                          Template Type <span className="text-danger">*</span>
                        </Form.Label>
                        <ESSearchDropdown
                          name="MasterTemplateId"
                          placeholder="Select Template Type"
                          options={SystemEmailTemplatesOptions}
                          required
                          isDisabled={!!id}
                        />
                      </Form.Group>



                      <Form.Group as={Col} md="3">
                        <Form.Label>
                          Template Name
                          <span className="text-danger">*</span>
                        </Form.Label>

                        <ESInput
                          className="form-control"
                          name="TempelateName"
                          placeholder="Enter template name"
                          type="text"
                          required
                          minLength={2}
                          maxLength={200}
                        />
                      </Form.Group>
                      <Form.Group as={Col} md="3">
                        <Form.Label>
                          Template Subject{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <ESInput
                          className="form-control"
                          name="TempelateSubject"
                          placeholder="Enter template subject"
                          type="text"
                          required
                          minLength={2}
                          maxLength={200}
                        />
                      </Form.Group>

                      <Col md="2">
                        <div className="form-check pt-4 mt-2">
                          <Controller
                            name="IsDefault"
                            control={methods.control}
                            render={({ field: { onChange, onBlur, value, ref } }) => (
                              <input
                                id="IsDefault"
                                type="checkbox"
                                className="form-check-input"
                                onChange={e => onChange(e.target.checked)}
                                onBlur={onBlur}
                                checked={!!value}
                                ref={ref}
                              />
                            )}
                          />
                          <label htmlFor="IsDefault" className="ms-2">Is Default</label>
                        </div>
                      </Col>
                    </Row>
                    <Form.Group as={Col} md="12">
                      <div className="label-buttons">
                        <Form.Label>
                          Template Labels <span className="text-danger">*</span>
                        </Form.Label>
                        <Button
                          variant="link"
                          onClick={() => copyToClipboard("[Full Name]")}
                        >
                          Full Name
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => copyToClipboard("[Organization Name]")}
                        >
                          Organization Name
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => copyToClipboard("[Form Link]")}
                        >
                          Form Link
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => copyToClipboard("[Form End Date]")}
                        >
                          Form End Date
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => copyToClipboard("[Form Name]")}
                        >
                          Form Name
                        </Button>
                      </div>
                      <Form.Label>
                        Template Body <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name={"TempelateBody"}
                        control={control}
                        rules={{ validate: validateQuillContent }}
                        render={({ field }) => (
                          <ReactQuill
                            {...field}
                            ref={quillRef}
                            value={field.value || ""}
                            // value={methods.watch("TempelateBody")}
                            onChange={(newContent) =>
                              methods.setValue("TempelateBody", newContent, { shouldDirty: true })
                            }
                            className="custom-quill"
                            modules={{
                              toolbar: [
                                [{ header: "1" }, { header: "2" }],
                                [
                                  "bold",
                                  "italic",
                                  "underline",
                                  "strike",
                                  "blockquote",
                                ],
                                [
                                  { list: "ordered" },
                                  { list: "bullet" },
                                  { indent: "-1" },
                                  { indent: "+1" },
                                ],
                                [{ script: "sub" }, { script: "super" }],
                                [{ color: [] }, { background: [] }],
                                [{ font: [] }],
                                [{ align: [] }],
                                ["link", "image", "video", "formula"],
                                ["clean"],
                                [{ direction: "rtl" }],
                                ["code-block"],
                                [
                                  {
                                    size: ["small", "medium", "large", "huge"],
                                  },
                                ],
                                ["emoji"],
                              ],
                            }}
                          />
                        )}
                      />
                      {errors &&
                        errors.TempelateBody &&
                        errors.TempelateBody.message && (
                          <span className="text-danger">
                            {errors.TempelateBody.message}
                          </span>
                        )}
                    </Form.Group>

                    <Row>
              <Col md="12">
                <Row>    <Col md="2">
 <div className="form-check">
                        <ESInput
                          name="IsActive"
                          type="checkbox"
                          placeholder={""}
                           className="form-check-input"
                        />
                        <label htmlFor="IsActive" className="form-check-label">Is Active</label>
                      </div>
                    </Col></Row>
              </Col>

                      <Col md="4">
                        <button
                          type="button"
                          className="save-btn"
                          onClick={methods.handleSubmit(
                            id ? handleEdit : handleCreate
                          )}
                          data-tour="save-template-info"
                        >
                          {id ? "Update" : "Save"}
                        </button>
                        <button
                          type="button"
                          className="back-btn"
                          onClick={() => navigate(tempelateMaster)}
                        >
                          Cancel
                        </button>
                      </Col>
                    </Row>
                  </form>
                </FormProvider>
              )}

              {showCreatePopup && (
                <ConfirmationPopup
                  show={showCreatePopup}
                  message="Are you sure you want to create this template?"
                  onConfirm={confirmCreateTemplate}
                  onCancel={() => setShowCreatePopup(false)}
                />
              )}

              {showEditPopup && (
                <ConfirmationPopup
                  show={showEditPopup}
                  message="Are you sure you want to edit this template?"
                  onConfirm={confirmEditTemplate}
                  onCancel={() => setShowEditPopup(false)}
                />
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default AddTemplate;
